import { useSelector } from 'react-redux';
import type {
  EmergencyCall,
  EmergencyCallLocation,
  EmergencyConversationLocation,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCalls.types';
import {
  apiTranscriptionToStateCall,
  MAX_CALL_START_TIME_OFFSET,
} from './transcriptionAdapter';
import type { RootState } from './transcriptionRootState';
import type { TranscriptionStateCall } from './transcriptionSlice.types';

/**
 * Returns the best transcription object for the currently selected call.
 */
export function useTranscriptionForSelectedCall() {
  return useSelector((state: RootState) => {
    const callID = state.emergencyCalls.selectedCallerID;
    return selectTranscriptionForCall(callID)(state);
  });
}

export function useAllKeywordMatches() {
  return useTranscriptionForSelectedCall()?.keyword_matches || [];
}

export const useSelectedKeywordMatch = () => {
  const matches = useAllKeywordMatches();
  const match = useTranscriptionForSelectedCall()?.selectedKeywordMatch;
  const matchSet = matches.find(m => m.keyword_set.id === match?.keywordSetID);
  if (!match || !matchSet) {
    return undefined;
  }
  return {
    matchIndex: match?.matchIndex,
    keywordSet: matchSet,
  };
};

function callStartTime(call?: EmergencyCall) {
  const firstLocation = (call?.core?.locations || [])[0];

  // Calls from search don't have locations or start times; a zero epoch
  // default allows the latest transcript we have to show up in such cases.
  return (
    (firstLocation as EmergencyConversationLocation)?.conversation_start_time ||
    (firstLocation as EmergencyCallLocation)?.call_start_time ||
    0
  );
}

const noTranscript = apiTranscriptionToStateCall(null);

/**
 * Given a callID, returns a selector. The selector takes root state and returns an
 * appropriate transcription if there is one, or an empty transcription if nothing
 * corresponds to this call.
 *
 * Uses the call_start_time to filter out transcripts for old calls.
 */
export function selectTranscriptionForCall(callID: string | null) {
  return (state: RootState): TranscriptionStateCall => {
    if (!callID) return noTranscript;
    const transcription = state.transcriptions?.calls[callID];
    const call = state.emergencyCalls.callQueue[callID];
    const startTime = callStartTime(call);

    const transcriptionStartTime =
      transcription?.payload?.call_information?.call_start_time || startTime;

    if (startTime - transcriptionStartTime < MAX_CALL_START_TIME_OFFSET) {
      return transcription || noTranscript;
    }
    return noTranscript;
  };
}

/**
 * Every transcript loaded so far for this caller, newest first — including
 * the current call's, so the list lines up with `selectTranscriptionTotalForCall`.
 */
export function selectTranscriptionHistoryForCall(callID: string | null) {
  return (state: RootState): TranscriptionStateCall[] => {
    if (!callID) return [];

    const transcriptions =
      state.transcriptions?.transcriptionLists?.[callID] || [];

    // Transcriptions are already stored sorted by start_time desc, but keep it safe.
    const sorted = [...transcriptions].sort((a, b) => {
      const bt = Number(b?.call_information?.call_start_time || 0);
      const at = Number(a?.call_information?.call_start_time || 0);
      return bt - at;
    });

    return sorted.map(t => apiTranscriptionToStateCall(t));
  };
}

/**
 * Total number of transcripts rtt_egress has for this caller, across all pages.
 * Falls back to the loaded count until the first page reports `total_records`.
 */
export function selectTranscriptionTotalForCall(callID: string | null) {
  return (state: RootState): number => {
    if (!callID) return 0;
    const loaded = state.transcriptions?.transcriptionLists?.[callID]?.length;
    const total = state.transcriptions?.transcriptionTotals?.[callID];
    return Math.max(total ?? 0, loaded ?? 0);
  };
}
